"use client";

import { useTranslations } from "next-intl";

import Image from "next/image";

import styles from "@/styles/page.module.scss";
import { useScroll, useTransform, motion } from "framer-motion";

import { AnimatedCard } from "@/components/AnimatedCard";
import SignUpForm from "@/components/SignUpForm";
import HydraIcon from "@/components/Icons/HydraIcon";
import { HydraButton } from "@/components/HydraButton";
import BranchIcon from "@/components/Icons/BranchIcon";
import { useEffect, useRef, useState } from "react";
import ParticlesBg from "@/components/Particles";
import TestimonialsCard from "@/components/TestimonialsCard";
import { testimonialCompaniesProfiles } from "@/components/TestimonialsCard/testimonialProfiles";
import { Footer } from "@/components/Footer";
import { CompanyCarousel } from "@/components/Carousel";
import BugCard from "@/components/BugCard";
import AuditCard from "@/components/AuditCard";
import IntegrationCard from "@/components/IntegrationCard";
import Link from "next/link";
import { PlatformCard } from "@/components/PlatformCard";
import { platforms } from "@/components/PlatformCard/platfroms";

export default function Home() {
  const t = useTranslations("Landing");

  const heroRef = useRef(null);
  const { scrollY } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const [activeTitle, setActiveTitle] = useState(true);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTitle((prev) => !prev);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const scale = useTransform(scrollY, [0, 500], [1, 0.8]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  const [isSticky, setIsSticky] = useState<number | null>(null);
  const testimonialsSectionRef = useRef<HTMLDivElement>(null);
  const contactSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && testimonialsSectionRef.current) {
          const rect = testimonialsSectionRef.current.getBoundingClientRect();
          const offsetTop = rect.top + window.scrollY;
          setIsSticky(offsetTop);
        } else {
          setIsSticky(null);
        }
      },
      {
        root: null,
        threshold: 0,
      }
    );
    if (contactSectionRef.current) {
      observer.observe(contactSectionRef.current);
    }
    return () => {
      if (contactSectionRef.current) {
        observer.unobserve(contactSectionRef.current);
      }
    };
  }, []);

  return (
    <main className="animate-fadeIn overflow-clip">
      <div
        className="mb-[-2rem] bg-gradient-light bg-custom-size bg-[-30vw_10vh] bg-no-repeat pb-[2rem] xl:bg-[-50vw_10vh]"
        style={{ "--gradient-angle": "210deg" } as React.CSSProperties}
      >
        <motion.div ref={heroRef} style={{ scale, opacity }}>
          <section id="hero">
            <div className="w-full min-h-[calc(100lvh-3rem)] mt-[-0.25rem] flex justify-center items-center flex-wrap p-[2rem] lg:gap-[5rem] gap-0">
              <div className="flex gap-[1rem] justify-center">
                <div className="flex max-w-[25rem] lg:max-w-[35rem] flex-col text-start p-8">
                  <div className="grid !grid-cols-1 max-w-[25rem] lg:max-w-[35rem]">
                    <h1
                      className="col-start-1 row-start-1 max-w-[20rem] lg:max-w-[35rem] transition-opacity duration-1000 text-start"
                      style={{ opacity: activeTitle ? 1 : 0 }}
                    >
                      {t("hero-title")}
                    </h1>
                    <h1
                      className="col-start-1 row-start-1 max-w-[20rem] lg:max-w-[35rem] transition-opacity duration-1000 text-start"
                      style={{ opacity: activeTitle ? 0 : 1 }}
                    >
                      {t("hero-title-2")}
                    </h1>
                  </div>

                  <h2 className="text-start mb-[1rem]">{t("hero-subtitle")}</h2>
                  <div className="flex gap-[1rem] items-center justify-center">
                    <div className="flex-1">
                      <HydraButton
                        id="contact-button"
                        type="button"
                        className="w-[10rem] sm:w-full"
                        onClick={() => {
                          const el = document.getElementById("contact");
                          if (el) el.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        {t("hero-cta-button-1")}
                      </HydraButton>
                    </div>
                    <div className="flex-1">
                      <HydraButton
                        id="call-button"
                        color="primary"
                        className="w-[10rem] sm:w-full"
                        onClick={() => {
                          window.open(
                            "https://cal.com/victor-hydraoss/15min",
                            "_blank"
                          );
                        }}
                      >
                        {t("hero-cta-button-2")}
                      </HydraButton>
                    </div>
                  </div>
                </div>

                <ParticlesBg />
              </div>
              <div className="mb-[1.5rem] block flex w-full items-end justify-end md:block md:hidden md:w-auto">
                <AnimatedCard
                  hasMaxW
                  rotation={12}
                  afterRotation={12}
                  rotationHover={0}
                  className="md:animate-rotateIn"
                >
                  <div className="flex h-[20vh] items-center justify-center px-[1rem] py-[1rem] pt-[1rem] md:h-[35vh] md:pb-[5rem] md:pt-[5rem]">
                    <HydraIcon
                      style={{
                        width: "auto",
                        maxWidth: "calc(100vw - 10rem)",
                        height: "100%",
                      }}
                    />
                  </div>
                </AnimatedCard>
              </div>
            </div>
          </section>
        </motion.div>
      </div>

      {/* <section id="section1" className="top-[4rem] md:sticky">
        <div
          className="flex min-h-[100vh] flex-col items-center justify-center rounded-b-none rounded-t-[2em] bg-gradient-purple px-8 py-16 lg:px-32"
          style={{ "--gradient-angle": "135deg" } as React.CSSProperties}
        >
          <div className="flex justify-center items-center gap-8 lg:flex-row lg:gap-[8rem]">
            <div className="max-w-[30rem]">
              <h1 className="text-center text-white lg:text-left">
                {t("section1-title")}
              </h1>
            </div>
            <div className="bg-purple-custom flex flex-col items-center justify-start gap-6 p-4">
              {" "}
              {platforms.map((platform, index) => (
                <PlatformCard key={platform.name} {...platform} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section> */}
      <section id="section2" className="top-[4rem] md:sticky">
        <div
          className="flex lg:min-h-[100vh] flex-col items-center justify-center rounded-b-none rounded-t-[2em] bg-gradient-purple px-8 py-16 lg:px-32"
          style={{ "--gradient-angle": "135deg" } as React.CSSProperties}
        >
          <div className="flex flex-col-reverse gap-8 lg:flex-row lg:gap-[8rem] items-center">
            <div className="w-fit content-end md:m-0 md:content-normal">
              <BugCard />
            </div>

            <div className="max-w-[30rem]">
              <h1 className="text-center text-white lg:text-left">
                {t("section2-title")}
              </h1>
            </div>
          </div>
        </div>
      </section>
      <section id="section3" className="top-[4rem] md:sticky">
        <div
          className="mt-[-2rem] py-16 flex items-center justify-center rounded-b-none rounded-t-[2em] bg-gradient-light bg-custom-size bg-[-30vw_-10vh] bg-no-repeat px-8 lg:px-32 pb-[8rem] pt-[2rem] lg:min-h-[100vh] lg:items-center lg:bg-[-50vw_-25vh] lg:py-[0]"
          style={{ "--gradient-angle": "310deg" } as React.CSSProperties}
        >
          <div className="w-full flex-col">
            <div className="my-[2rem] flex w-full flex-col items-center gap-8 lg:mt-[6rem] lg:flex-row  lg:justify-center lg:gap-[8rem]">
              <div className="flex max-w-[35rem] flex-col">
                <h1 className="mb-4 text-center text-primary lg:text-left">
                  {t("section3-title")}
                </h1>
              </div>
              <AuditCard />
            </div>
          </div>
        </div>
      </section>
      <section
        id="section4"
        ref={testimonialsSectionRef}
        style={{
          position: isSticky ? "sticky" : "relative",
          top: isSticky !== null ? `${isSticky - window.scrollY}px` : "auto",
          transition: "top 0.3s ease",
        }}
      >
        <div
          className="lg:min-h-[100vh] mt-[-2rem] py-16 rounded-[2em] px-8 lg:px-32 rounded-b-none rounded-t-[2em] bg-gradient-purple p-6 pb-[8rem] pt-[2rem] lg:p-[2rem] lg:py-[5rem]"
          style={{ "--gradient-angle": "100deg" } as React.CSSProperties}
        >
          <div className="lg:mt-[5rem] justify-center items-center flex flex-col lg:flex-row-reverse gap-8 lg:gap-[8rem] lg:mb-[10rem]">
            <div className="lg:max-w-[35rem]">
              <h1 className="text-center text-white md:text-left">
                {t("section4-title")}
              </h1>
            </div>

            <IntegrationCard />
          </div>
          <div className="flex justify-center">
            <div className="relative mt-[4rem] flex flex-wrap justify-center gap-4 md:mt-[12rem] md:max-w-[80rem] lg:gap-8 lg:p-16">
              <div className="block md:hidden">
                <CompanyCarousel />
              </div>
              {testimonialCompaniesProfiles.map((profile, index) => (
                <div
                  key={profile.name}
                  className={`relative hidden md:block ${
                    // Mobile/tablet layout
                    index === 1 || index === 3 ? "mt-[-10rem]" : ""
                  } ${
                    // Desktop layout
                    index === 2
                      ? "lg:mt-[10rem]"
                      : index === 3
                        ? "lg:mt-[-20rem]"
                        : ""
                  } ${
                    // Tablet layout
                    index === 2
                      ? "md:mt-[0.5rem]"
                      : index === 3
                        ? "md:mt-[-15rem]"
                        : ""
                  } ${
                    // Control width to create columns with larger gap
                    "w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-2rem)]"
                  }`}
                  style={{
                    filter: "drop-shadow(0 0 20px rgba(147, 51, 234, 0.3))",
                  }}
                >
                  <TestimonialsCard index={index} {...profile} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        id="contact"
        ref={contactSectionRef}
        className="relative mt-[-2rem] flex min-h-[calc(100vh-4rem)] flex-col justify-between overflow-hidden rounded-b-none rounded-t-[2em] bg-gradient-purple"
        style={{ "--gradient-angle": "20deg" } as React.CSSProperties}
      >
        <div className="min-w-full flex-row flex-col items-baseline justify-center gap-8 overflow-hidden p-6 pt-[2rem] md:flex md:flex-row">
          <BranchIcon
            className="absolute left-[-25%] top-[-35%] h-[150%] w-[150%] scale-x-[-1] md:z-10"
            fill="#FDF9ED"
            stroke="#FDF9ED"
          />
          <div>
            <h1 className="max-w-[35rem] text-center text-white md:text-left">
              {t("signUp-title")}
            </h1>
            {/* <h2 className="max-w-[35rem] text-center text-xl font-medium text-white md:text-left">
                {t("signUp-cta")}
              </h2> */}
          </div>

          <SignUpForm className="z-20 my-[2rem]" />
        </div>
        <Footer className="!m-0" />
      </section>
    </main>
  );
}
